import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Ban, BellRing, X, FileText, UserCheck, ClipboardCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 25_000; // 25 seconds — light enough not to flood Mongo
const LS_KEY = (cid) => `portal-last-seen-event-at-${cid}`;

/**
 * Realtime notification popup for the Contractor Portal.
 *
 * Polls /contractor-auth/me + /contractor-auth/workmen every 25 s and
 * compares record-level approved_at / rejected_at (and
 * pay_roster_approved_at / pay_roster_rejected_at for payroll users)
 * against the last-seen timestamp persisted in localStorage. New events
 * surface in two places:
 *   1) Sonner toast (auto-dismiss).
 *   2) A centered acknowledgement modal listing every unread event with the
 *      admin's name + reason (if rejected) — the contractor must click
 *      "Acknowledge" to mark them read.
 *
 * Polling only runs while the tab is visible & online, to be polite on
 * mobile data plans on construction sites.
 */
export default function PortalEventNotifier({
  contractor,
  workmen,
  authHeader,
  onContractor,
  onWorkmen,
}) {
  const cid = contractor?.id;
  const isPayroll = !!contractor?.is_payroll;
  const [events, setEvents] = useState([]); // [{id, kind, status, name, at, by, reason, icon}]
  const lastSeenRef = useRef(0);

  // Initialise the watermark — on the very first mount we DO NOT alert about
  // historical approvals; we just remember "everything until now is seen".
  useEffect(() => {
    if (!cid) return;
    const raw = localStorage.getItem(LS_KEY(cid));
    if (raw && !isNaN(Number(raw))) {
      lastSeenRef.current = Number(raw);
    } else {
      const seedMax = computeMaxEventTs(contractor, workmen);
      const seed = seedMax || Date.now();
      lastSeenRef.current = seed;
      localStorage.setItem(LS_KEY(cid), String(seed));
    }
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a "newer than watermark" event list from a contractor + workmen snapshot.
  const buildEventsFrom = useCallback((c, wm) => {
    if (!c) return [];
    const watermark = lastSeenRef.current || 0;
    const out = [];
    // Payroll: pay_roster_status approval/rejection
    if (c.is_payroll) {
      const aAt = parseIso(c.pay_roster_approved_at);
      if (c.pay_roster_status === "approved" && aAt > watermark) {
        out.push({
          id: `pr-approved-${aAt}`,
          kind: "pay-roster",
          status: "approved",
          at: aAt,
          name: c.name || "Your record",
          by: c.pay_roster_approved_by_name || "Admin",
          reason: null,
        });
      }
      const rAt = parseIso(c.pay_roster_rejected_at);
      if (c.pay_roster_status === "rejected" && rAt > watermark) {
        out.push({
          id: `pr-rejected-${rAt}`,
          kind: "pay-roster",
          status: "rejected",
          at: rAt,
          name: c.name || "Your record",
          by: c.pay_roster_rejected_by_name || "Admin",
          reason: c.pay_roster_rejection_reason || null,
        });
      }
    }
    // Workmen Roster (Contractor users only — payroll users don't manage a crew)
    (wm || []).forEach((w) => {
      const aAt = parseIso(w.approved_at);
      if (w.status === "approved" && aAt > watermark) {
        out.push({
          id: `wm-approved-${w.id}-${aAt}`,
          kind: "workman",
          status: "approved",
          at: aAt,
          name: w.name || "Workman",
          by: w.approved_by_name || "Admin",
          reason: null,
        });
      }
      const rAt = parseIso(w.rejected_at);
      if (w.status === "rejected" && rAt > watermark) {
        out.push({
          id: `wm-rejected-${w.id}-${rAt}`,
          kind: "workman",
          status: "rejected",
          at: rAt,
          name: w.name || "Workman",
          by: w.rejected_by_name || "Admin",
          reason: w.rejection_reason || null,
        });
      }
    });
    return out.sort((a, b) => b.at - a.at);
  }, []);

  // Bind a Sonner toast per event so the contractor sees an alert even if the
  // modal is dismissed quickly. Each toast carries a stable id so we don't
  // double-fire on subsequent polls.
  const firedToasts = useRef(new Set());
  const fireToasts = useCallback((evs) => {
    evs.forEach((e) => {
      if (firedToasts.current.has(e.id)) return;
      firedToasts.current.add(e.id);
      const headline =
        e.kind === "pay-roster"
          ? (e.status === "approved" ? "Pay-Roster approved" : "Pay-Roster rejected")
          : (e.status === "approved" ? `Workman approved · ${e.name}` : `Workman rejected · ${e.name}`);
      const subtitle = e.status === "rejected" && e.reason ? `Reason: ${e.reason}` : `By ${e.by}`;
      if (e.status === "approved") toast.success(headline, { description: subtitle });
      else toast.error(headline, { description: subtitle });
    });
  }, []);

  // First-paint snapshot evaluation (handy when the user opens the portal AFTER
  // an admin already approved something while they were offline).
  useEffect(() => {
    if (!cid) return;
    const evs = buildEventsFrom(contractor, workmen);
    if (evs.length) {
      fireToasts(evs);
      setEvents(evs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  // Polling loop — only when the page is visible and the network is online.
  useEffect(() => {
    if (!cid) return;
    let timer = null;
    let cancelled = false;
    const poll = async () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      try {
        const me = await axios.get(`${API}/contractor-auth/me`, { headers: authHeader });
        const c2 = me.data;
        let wm2 = [];
        if (!c2?.is_payroll) {
          try {
            const w = await axios.get(`${API}/contractor-auth/workmen`, { headers: authHeader });
            wm2 = w.data || [];
          } catch { /* ignore */ }
        }
        if (cancelled) return;
        const evs = buildEventsFrom(c2, wm2);
        // Update parent state so the rest of the portal reflects new
        // is_active / status / printables-unlock state immediately.
        onContractor?.(c2);
        if (!c2?.is_payroll) onWorkmen?.(wm2);
        if (evs.length) {
          fireToasts(evs);
          setEvents((cur) => mergeEvents(cur, evs));
        }
      } catch { /* ignore — likely a brief network blip */ }
    };
    poll();
    timer = setInterval(poll, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [cid, authHeader, buildEventsFrom, fireToasts, onContractor, onWorkmen]);

  function acknowledge() {
    if (!cid) return;
    const max = events.reduce((m, e) => Math.max(m, e.at), lastSeenRef.current);
    lastSeenRef.current = max + 1; // +1 so a rapid duplicate poll doesn't refire
    localStorage.setItem(LS_KEY(cid), String(lastSeenRef.current));
    setEvents([]);
  }

  if (!events.length) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 print:hidden" data-testid="portal-event-notifier" onClick={acknowledge}>
      <div
        className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b-2 border-slate-900 bg-ncc-navy px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-ncc-gold animate-pulse" />
            <div>
              <div className="industrial-label text-[10px] text-ncc-gold2">// New Notifications</div>
              <div className="font-heading text-lg font-extrabold">
                {events.length === 1 ? "1 status update" : `${events.length} status updates`}
              </div>
            </div>
          </div>
          <button type="button" onClick={acknowledge} className="rounded-md p-1 hover:bg-white/10" data-testid="portal-event-notifier-close">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-200">
          {events.map((e) => (
            <article key={e.id} data-testid={`portal-event-${e.kind}-${e.status}`} className="flex items-start gap-3 px-4 py-3">
              <div className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 ${
                e.status === "approved" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-red-500 bg-red-50 text-red-600"
              }`}>
                {e.status === "approved"
                  ? <BadgeCheck className="h-4 w-4" />
                  : <Ban className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {e.kind === "pay-roster" ? <ClipboardCheck className="h-3.5 w-3.5 text-ncc-navy" /> : <UserCheck className="h-3.5 w-3.5 text-ncc-navy" />}
                  <span className="industrial-label text-[10px] text-slate-500">
                    {e.kind === "pay-roster" ? "Pay-Roster (Medical / Induction)" : "Workmen Roster"}
                  </span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                    e.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                  }`}>
                    {e.status.toUpperCase()}
                  </span>
                </div>
                <div className="mt-1 font-heading text-sm font-extrabold text-slate-900 truncate">{e.name}</div>
                <div className="font-mono text-[11px] text-slate-600">
                  By <b className="text-ncc-navy">{e.by}</b> · {formatRel(e.at)}
                </div>
                {e.status === "rejected" && e.reason && (
                  <div className="mt-1 flex items-start gap-1 rounded border border-red-200 bg-red-50 px-2 py-1 font-mono text-[11px] text-red-700">
                    <FileText className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{e.reason}</span>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-4 py-3">
          <button
            type="button"
            onClick={acknowledge}
            data-testid="portal-event-notifier-acknowledge"
            className="inline-flex items-center gap-2 border-2 border-ncc-navy bg-ncc-gold px-3 py-1.5 industrial-label text-[11px] text-ncc-navy hover:-translate-y-0.5 hover:shadow-brutal-sm"
          >
            <BadgeCheck className="h-3.5 w-3.5" /> Acknowledge
          </button>
        </footer>
      </div>
    </div>
  );
}

// ===== helpers =====

function parseIso(v) {
  if (!v) return 0;
  const t = Date.parse(v);
  return isNaN(t) ? 0 : t;
}

function computeMaxEventTs(c, wm) {
  let max = 0;
  if (c) {
    max = Math.max(
      max,
      parseIso(c.pay_roster_approved_at),
      parseIso(c.pay_roster_rejected_at),
      parseIso(c.approved_at),
      parseIso(c.rejected_at),
    );
  }
  (wm || []).forEach((w) => {
    max = Math.max(max, parseIso(w.approved_at), parseIso(w.rejected_at));
  });
  return max;
}

function mergeEvents(cur, incoming) {
  const seen = new Set(cur.map((e) => e.id));
  const next = [...cur];
  incoming.forEach((e) => { if (!seen.has(e.id)) next.push(e); });
  return next.sort((a, b) => b.at - a.at);
}

function formatRel(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  const d = new Date(ts);
  return d.toLocaleString();
}
